import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Card,
  Grid,
  MenuItem,
  Select as MuiSelect,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Select from "react-select";
import ButtonCustom from "./ButtonCustom";
import { Search, Refresh, FileDownload } from "@mui/icons-material";
import { capitalise, getTodayDate } from "../utils/tableFunctions";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { COLORS } from "../utils/colors";
import { LoadingButton } from "@mui/lab";
import * as XLSX from "xlsx";
import { styles } from "../utils/styles";

const textFieldStyles = {
  "& .MuiFormLabel-root": {
    lineHeight: "14px",
    fontSize: "14px",
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    height: "14px",
  },
};

const CommonFormGroup = ({ label, children }) => {
  return (
    <>
      <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 14 }} variant="subtitle1">
        {label}
      </Typography>
      {children}
    </>
  );
};

const Cell = ({ field, data, errors, onChange }) => {
  const {
    options,
    inputValues,
    setInputValues,
    label,
    size,
    param,
    loading,
    id = true,
    width,
    value,
  } = field;
  let formGroup;

  switch (field.type) {
    case "date":
      formGroup = (
        <CommonFormGroup label={label}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              disableFuture
              inputFormat="YYYY-MM-DD"
              value={data[param] || value || null}
              onChange={(newValue) => onChange(param, newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={errors[param]}
                  helperText={errors[param]}
                  size="small"
                  fullWidth
                  sx={textFieldStyles}
                />
              )}
            />
          </LocalizationProvider>
        </CommonFormGroup>
      );
      break;

    case "month":
      formGroup = (
        <CommonFormGroup label={label}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              disableFuture
              views={["year", "month"]}
              openTo="month"
              inputFormat="YYYY-MM"
              value={data[param] || value || null}
              onChange={(newValue) => onChange(param, newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={errors[param]}
                  helperText={errors[param]}
                  size="small"
                  fullWidth
                  sx={textFieldStyles}
                />
              )}
            />
          </LocalizationProvider>
        </CommonFormGroup>
      );
      break;

    case "year":
      formGroup = (
        <CommonFormGroup label={label}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              views={["year"]}
              openTo="year"
              inputFormat="YYYY"
              value={(data[param] || value) ? moment({ year: data[param] || value }) : null}
              onChange={(newValue) => onChange(param, newValue ? newValue.year() : null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={errors[param]}
                  helperText={errors[param]}
                  size="small"
                  fullWidth
                  sx={textFieldStyles}
                />
              )}
            />
          </LocalizationProvider>
        </CommonFormGroup>
      );
      break;
  
    case "daterange":
      formGroup = (
        <CommonFormGroup label={label}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack direction="row" spacing={2}>
              <DatePicker
                label={"Start date"}
                disableFuture
                inputFormat="YYYY-MM-DD"
                value={data[param[0]] || null}
                onChange={(newValue) => onChange(param[0], newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={errors[param[0]]}
                    helperText={errors[param[0]]}
                    size="small"
                    fullWidth
                    sx={textFieldStyles}
                  />
                )}
              />
              <DatePicker
                label={"End date"}
                disableFuture
                shouldDisableDate={(date) => {
                  const startDate = data[param[0]];
                  return startDate ? date.isBefore(startDate, 'day') : false;
                }}
                inputFormat="YYYY-MM-DD"
                value={data[param[1]] || null}
                onChange={(newValue) => onChange(param[1], newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={errors[param[1]]}
                    helperText={errors[param[1]]}
                    size="small"
                    fullWidth
                    sx={textFieldStyles}
                  />
                )}
              />
            </Stack>
          </LocalizationProvider>
        </CommonFormGroup>
      );
      break;

    case "search-select":
      formGroup = (
        <CommonFormGroup label={label}>
          <Autocomplete
            loading={loading}
            loadingText="No result"
            noOptionsText="Options unavailable"
            fullWidth
            renderOption={(props, option) => {
              return (
                <li {...props} key={props.id + props.key}>
                  {option.id ? (option.label || "") : option}
                </li>
              );
            }}
            options={options || []}
            // getOptionLabel={(option) => option.label || option}
            value={data?.[param] || null}
            onChange={(e, value, reason) => {
              onChange(param, value)
              
              if (setInputValues && (reason === "clear" || reason === "selectOption")) {
                setInputValues((prev) => ({ ...prev, [param]: "" }));
              }
            }}

            {...(setInputValues
                ? {
                    inputValue: inputValues?.[param] || (data?.[param]?.id ? data[param].label : data[param]) || "",
                    onInputChange: (e, val, reason) => {
                      if (reason === "input" && setInputValues) {
                        setInputValues((prev) => ({
                          ...prev,
                          [param]: val,
                        }));
                      }
                    },
                  }
                : {})
            }
            renderInput={(params) => (
              <TextField
                {...params}
                error={errors[param]}
                helperText={errors[param]}
                size={size || "small"}
                placeholder="Search..."
                sx={textFieldStyles}
              />
            )}
          />
        </CommonFormGroup>
      );
      break;

    case "select":
      formGroup = (
        <CommonFormGroup label={label}>
          <MuiSelect
            error={errors[param]}
            fullWidth
            value={data[param] ?? ""}
            onChange={(e) => onChange(param, e.target.value)}
            size="small"
            renderValue={(selected) => {
              if (selected === "") {
                return <Box sx={{ color: "#919EAB" }}>Select</Box>;
              }
              const item = options?.find((v) =>
                !id ? v === selected : v.id === selected
              );
              return !id ? item : item.name;
            }}
            displayEmpty
            sx={{
              height: "30px",
              "& .MuiSelect-select": {
                fontSize: "14px",
              },
            }}
          >
            {options && options.length > 0 ? (
              options.map((item) => (
                <MenuItem
                  key={!id ? item : item.id}
                  value={!id ? item : item.id}
                >
                  {capitalise(!id ? item : item.name)}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loading ? "Loading..." : "Options unavailable"}
              </MenuItem>
            )}
          </MuiSelect>
        </CommonFormGroup>
      );
      break;

    case "multi-select":
      formGroup = (
        <CommonFormGroup label={label}>
          <Select
            closeMenuOnSelect={false}
            value={options?.filter(({ value }) => data[param]?.includes(value))}
            isMulti
            options={options || []}
            onChange={(event, { action }) => {
              onChange(param, event.map((selected) => selected.value));

              if (action === "clear" && setInputValues) {
                setInputValues((prev) => ({ ...prev, [param]: "" }));
              }
            }}
            {...(setInputValues
                ? {
                    inputValue: inputValues?.[param] || "",
                    onInputChange: (val, { action }) => {
                      setInputValues((prev) => ({
                        ...prev,
                        [param]:
                          action === "input-change"
                            ? val
                            : action === "set-value" && inputValues?.[param]
                            ? ""
                            : prev[param],
                      }));
                    },
                  }
                : {})
            }
            menuPortalTarget={document.body}
            styles={{
              control: (provided) => ({
                ...provided,
                minHeight: "30px",
              }),
              dropdownIndicator: (provided) => ({
                ...provided,
                paddingTop: 0,
                paddingBottom: 0
              }),
            }}
          />
        </CommonFormGroup>
      );
      break;

    case "text":
      formGroup = (
        <CommonFormGroup label={label}>
          <TextField
            size="small"
            type="text"
            fullWidth
            value={data[param] || ""}
            onChange={(e) => onChange(param, e.target.value)}
            error={errors[param]}
            helperText={errors[param]}
            sx={textFieldStyles}
          />
        </CommonFormGroup>
      );
      break;
    
      case "number":
        formGroup = (
          <CommonFormGroup label={label}>
            <TextField
              size="small"
              type="number"
              fullWidth
              value={data[param] || ""}
              onChange={(e) => onChange(param, e.target.value)}
              error={errors[param]}
              helperText={errors[param]}
              sx={textFieldStyles}
            />
          </CommonFormGroup>
        );
        break;
  }

  return (
    <Grid key={param} item xs={12} sm={12} md={width || 3} lg={width || 3}>
      {formGroup}
    </Grid>
  );
};

const CSVButton = ({ filename, getCSVData }) => {
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    if (!getCSVData) return;
    setLoading(true);
    getCSVData()
      .then((e) => downloadExcel(e, filename))
      .then(() => setLoading(false));
  };

  const downloadExcel = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, filename);
  };

  return (
    <LoadingButton
      variant="outlined"
      onClick={fetchData}
      loading={loading}
      size="small"
      startIcon={<FileDownload />}
      sx={{ ml: 1, mt: 2 }}
      disabled={!getCSVData}
    >
      Download
    </LoadingButton>
  );
};

const DownloadButton = ({ onDownload }) => {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    if (!onDownload) return;

    setLoading(true);
    try {
      await onDownload();
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadingButton
      variant="outlined"
      onClick={handleDownload}
      loading={loading}
      size="small"
      startIcon={<FileDownload />}
      sx={{ ml: 1, mt: 2 }}
    >
      Download
    </LoadingButton>
  );
};

const FormSearch = ({
  schema,
  onSearch,
  getCSVData = false,
  type,
  csv,
  defaultValue,
  disabled = false,
  onChangeBusinessEntity = () => {},
  onReset,
  onDownload,
  errors: externalErrors,
  filename,
  ...rest
}) => {
  const [data, setData] = useState({ ...defaultValue });
  const [errors, setErrors] = useState({});
  const displayErrors = externalErrors || errors;

  useEffect(() => {
    setData({ ...defaultValue });
  }, [defaultValue]);

  const getFileName = () => {
    if (filename) return filename;
    return type + "-" + getTodayDate() + ".xlsx";
  };

  const onHandleChange = (fieldName, value) => {
    const newData = { ...data };
    if (value === null || value === undefined || /^\s*$/.test(value)) {
      delete newData[fieldName];
    } else {
      newData[fieldName] = value;
    }

    setData(newData);
    onChangeBusinessEntity && onChangeBusinessEntity(newData);
  };

  return (
    <Card sx={{ mb: 2, px: 2, py: 2 }}>
      <Grid container spacing={2}>
        {schema.map((item, index) => (
          <Cell
            key={index}
            field={{ ...item, ...rest }}
            data={data}
            errors={displayErrors}
            onChange={onHandleChange}
          />
        ))}
      </Grid>

      <Stack direction="row" justifyContent="flex-end">
        <ButtonCustom
          disabled={disabled}
          sx={{ ml: 1, mt: 2, ...styles.customButton }}
          icon={<Search />}
          variant="contained"
          title="Search"
          onClick={() => onSearch(data)}
        />
        <ButtonCustom
          sx={{ ml: 1, mt: 2 }}
          icon={<Refresh />}
          title="Reset"
          onClick={() => {
            if (onReset) {
              onReset();
              setData({});
            } else {
              setData({ ...defaultValue });
            }
          }}
        />
        {onDownload && <DownloadButton onDownload={onDownload} />}
        {csv && (
          <CSVButton
            filename={getFileName()}
            getCSVData={getCSVData}
            disabled={getCSVData}
          />
        )}
      </Stack>
    </Card>
  );
};

export default FormSearch;
